import type { Vector } from '../shared/vector.ts';

import { WEAPON_NAMES } from '../shared/weapons.ts';
import type { WeaponName } from '../shared/weapons.ts';

/** The files in `public/sounds`. A weapon's sound is named after the weapon. */
export type SoundName = WeaponName | 'reload' | 'join';

const SOUNDS: SoundName[] = [...WEAPON_NAMES, 'reload', 'join'];

const STORAGE_KEY = 'nozombie.muted';
const MASTER_VOLUME = 0.6;

/** Within this many pixels a sound is here, and plays whole. */
const FULL_VOLUME_RANGE = 250;

/**
 * A cap on the shots ringing at once. A rifle fires twenty times a second, so
 * a firefight reaches this in a blink; past it there is nothing left to pick
 * apart, and dropping a shot is cheaper than mixing one nobody can hear.
 */
const MAX_VOICES = 24;

/**
 * One `AudioContext`, the files decoded once, and a throwaway source node per
 * shot — which is what those nodes are for. An `<audio>` element each would
 * cut the previous shot off, and a game where the shots overlap needs them to
 * overlap.
 *
 * The game is top-down, so the ear is the local player: a sound fades with the
 * distance to them and leans to the side of the screen it came from. Off the
 * screen it does not travel at all.
 */
export function createAudio() {
  const context = new AudioContext();
  const buffers = new Map<SoundName, AudioBuffer>();

  let muted = localStorage.getItem(STORAGE_KEY) === 'true';
  let voices = 0;

  const master = new GainNode(context, { gain: muted ? 0 : MASTER_VOLUME });
  master.connect(context.destination);

  for (const name of SOUNDS) {
    load(name).catch(error => console.warn(`No ${name} sound:`, error));
  }

  // The browser holds a fresh context suspended until the page is touched, and
  // nothing throws to say so: without this the game is silent and looks fine.
  window.addEventListener('click', wake);
  window.addEventListener('keydown', wake);
  window.addEventListener('keydown', toggleOnM);

  return { play, isMuted: () => muted };

  function play(name: SoundName, at: Vector, listener: Vector) {
    const buffer = buffers.get(name);

    if (!buffer || muted || voices >= MAX_VOICES) return;
    if (context.state !== 'running') return;

    const offset = at.sub(listener);
    const earshot = earshotRadius();
    const volume = volumeAt(offset.magnitude, earshot);

    if (volume <= 0) return;

    const source = new AudioBufferSourceNode(context, { buffer });
    const gain = new GainNode(context, { gain: volume });
    const panner = new StereoPannerNode(context, {
      pan: clamp(offset.x / (window.innerWidth / 2), -1, 1),
    });

    source.connect(gain).connect(panner).connect(master);

    voices++;
    source.onended = () => voices--;
    source.start();
  }

  async function load(name: SoundName) {
    const response = await fetch(`/sounds/${name}.mp3`);

    if (!response.ok) throw new Error(`${response.status} for ${name}.mp3`);

    buffers.set(
      name,
      await context.decodeAudioData(await response.arrayBuffer()),
    );
  }

  function wake() {
    if (context.state === 'suspended') void context.resume();
  }

  function toggleOnM(event: KeyboardEvent) {
    if (event.code !== 'KeyM' || event.repeat) return;

    muted = !muted;
    master.gain.value = muted ? 0 : MASTER_VOLUME;
    localStorage.setItem(STORAGE_KEY, String(muted));
  }
}

/** Half the diagonal of the window: the far corner is the edge of hearing. */
function earshotRadius() {
  return Math.hypot(window.innerWidth, window.innerHeight) / 2;
}

/**
 * Full volume nearby, silent at the edge of the screen, and a square root in
 * between: gain is not loudness. Handing the fraction of the distance straight
 * to a `GainNode` leaves a shot at the side of the screen at a sixth of the
 * volume, which reads as nothing happening rather than as something far away.
 */
function volumeAt(distance: number, earshot: number) {
  // On a small window the whole screen fits inside the range that plays whole,
  // and the fade would run backwards: louder than 1, and never reaching zero.
  const whole = Math.min(FULL_VOLUME_RANGE, earshot / 2);

  if (distance <= whole) return 1;

  const left = 1 - (distance - whole) / (earshot - whole);
  return left <= 0 ? 0 : Math.sqrt(left);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}
